import React, { useState, useEffect, useMemo } from 'react';
import { coursesAPI, courseCategoriesAPI, modulesAPI } from '../services/api';
import Modal from '../components/Modal';
import SearchableSelect from '../components/Form/SearchableSelect';
import LoadingSpinner from '../components/LoadingSpinner';
import MessageDisplay from '../components/MessageDisplay';
import useAsyncOperation from '../hooks/useAsyncOperation';
import StaffPageShell from '../components/StaffPageShell';
import useTrainingCenters, { centerOptionsWithFallback } from '../hooks/useTrainingCenters';
import {
  buildCourseSubmitData,
  extractArrayFromApiResponse,
  filterValidCourseCategories,
  getCourseCategoryDisplayName,
  mapCourseModulesFromCourse
} from './courseManagementUtils';

const CourseManagementPage = () => {
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allModules, setAllModules] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showModulesModal, setShowModulesModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const { loading, error, success, execute, setError, setSuccess } = useAsyncOperation();
  const [editingCourse, setEditingCourse] = useState(null);
  const [formData, setFormData] = useState({
    courseName: '',
    courseCode: '',
    categoryId: '',
    description: '',
    durationDays: '',
    deliveryMode: 'Online',
    offlineCenter: '',
    isActive: true
  });

  const [courseModules, setCourseModules] = useState([]);
  const [availableModules, setAvailableModules] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [deliveryModeFilter, setDeliveryModeFilter] = useState('All');
  const { options: catalogCenters } = useTrainingCenters();
  const [moduleToAdd, setModuleToAdd] = useState('');

  useEffect(() => {
    // Clear any stale success messages when component mounts
    setSuccess('');
    fetchCourses();
    fetchCategories();
    fetchAllModules();
  }, []);

  const fetchCourses = async () => {
    try {
      // Fetch directly without execute to avoid error swallowing
      const response = await coursesAPI.getAll();
      console.log('📚 CourseManagementPage - Raw API response:', response);
      
      if (response?.data) {
        console.log('📚 CourseManagementPage - response.data:', response.data);
        const coursesData = extractArrayFromApiResponse(response.data);
        console.log('📚 CourseManagementPage - Setting courses:', coursesData.length, coursesData);
        setCourses(Array.isArray(coursesData) ? coursesData : []);
      } else {
        console.warn('⚠️ CourseManagementPage - No response.data, setting empty array');
        setCourses([]);
      }
    } catch (err) {
      console.error('❌ CourseManagementPage - Error fetching courses:', err);
      console.error('❌ Error details:', err.response?.data || err.message);
      setError(`Failed to fetch courses: ${err.response?.data?.message || err.message || 'Unknown error'}`);
      setCourses([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await courseCategoriesAPI.getAll();
      const categoriesData = extractArrayFromApiResponse(response.data);
      
      // Debug: Log what we're getting
      console.log('📋 Course Categories fetched:', categoriesData);
      
      // Filter to only show course category types (PO, SLP, JOU, TI, WEBINAR, WS)
      // These must have both categoryCode and categoryType fields
      const validCategories = filterValidCourseCategories(categoriesData);
      
      console.log('✅ Valid course category types:', validCategories);
      
      if (validCategories.length === 0) {
        console.warn('⚠️ No course category types found. Please create categories with categoryType (PO, SLP, JOU, TI, WEBINAR, WS) in Course Category Management.');
      }
      
      setCategories(validCategories);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setCategories([]);
    }
  };

  const fetchAllModules = async () => {
    try {
      const response = await modulesAPI.getActive();
      setAllModules(extractArrayFromApiResponse(response.data));
    } catch (err) {
      console.error('Error fetching modules:', err);
    }
  };

  const handleCreate = () => {
    setEditingCourse(null);
    setFormData({
      courseName: '',
      courseCode: '',
      categoryId: '',
      description: '',
      durationDays: '',
      deliveryMode: 'Online',
      offlineCenter: '',
      isActive: true
    });
    setShowModal(true);
  };

  const handleEdit = (course) => {
    setEditingCourse(course);
    setFormData({
      courseName: course.courseName || '',
      courseCode: course.courseCode || '',
      categoryId: course.categoryId?.toString() || '',
      description: course.description || '',
      durationDays: course.durationDays?.toString() || '',
      deliveryMode: course.deliveryMode || 'Online',
      offlineCenter: course.offlineCenter || '',
      isActive: course.isActive !== undefined ? course.isActive : true
    });
    setShowModal(true);
  };

  const handleManageModules = async (course) => {
    setSelectedCourse(course);
    try {
      const response = await coursesAPI.getById(course.id);
      const courseData = response.data?.data || response.data || course;
      const mappedModules = mapCourseModulesFromCourse(courseData);
      setCourseModules(mappedModules);
      
      // Get available modules (not already in course)
      const assignedModuleIds = mappedModules.map((cm) => cm.moduleId);
      setAvailableModules(allModules.filter(m => !assignedModuleIds.includes(m.id)));
      
      setShowModulesModal(true);
    } catch (err) {
      console.error('Error fetching course modules:', err);
      setCourseModules([]);
      setAvailableModules(allModules);
      setShowModulesModal(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const { submitData, error: submitError } = buildCourseSubmitData(formData);
    if (submitError) {
      setError(submitError);
      return;
    }
    
    console.log('📝 Submitting course data:', submitData);
    
    // Clear previous field errors
    setFieldErrors({});
    
    try {
      const response = await execute(async () => {
        if (editingCourse) {
          return await coursesAPI.update(editingCourse.id, submitData);
        } else {
          return await coursesAPI.create(submitData);
        }
      }, { 
        successMessage: `Course ${editingCourse ? 'updated' : 'created'} successfully!`,
        throwOnError: true
      });
      
      if (response) {
        setShowModal(false);
        setEditingCourse(null);
        setFieldErrors({});
        fetchCourses();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      // Handle validation errors
      if (err?.response?.status === 400 && err?.response?.data?.error?.details) {
        const validationErrors = {};
        err.response.data.error.details.forEach((detail) => {
          validationErrors[detail.field] = detail.message;
        });
        setFieldErrors(validationErrors);
        
        // Also set a general error message
        const errorMessages = err.response.data.error.details
          .map(d => `${d.field}: ${d.message}`)
          .join('\n');
        setError(`Validation failed:\n${errorMessages}`);
      } else {
        // Handle other errors
        const errorMessage = err?.response?.data?.error?.message || 
                            err?.response?.data?.message || 
                            err?.message || 
                            'An error occurred while saving the course';
        setError(errorMessage);
      }
    }
  };

  const handleAddModule = (moduleId) => {
    const module = allModules.find(m => m.id === moduleId);
    if (module && !courseModules.find(cm => cm.moduleId === moduleId)) {
      setCourseModules([...courseModules, {
        moduleId: module.id,
        moduleName: module.moduleName,
        order: courseModules.length
      }]);
      setAvailableModules(availableModules.filter(m => m.id !== moduleId));
    }
  };

  const handleRemoveModule = (moduleId) => {
    const module = courseModules.find(cm => cm.moduleId === moduleId);
    if (module) {
      setCourseModules(courseModules.filter(cm => cm.moduleId !== moduleId));
      const moduleData = allModules.find(m => m.id === moduleId);
      if (moduleData) {
        setAvailableModules([...availableModules, moduleData]);
      }
    }
  };

  const handleUpdateModuleOrder = (moduleId, direction) => {
    const index = courseModules.findIndex(cm => cm.moduleId === moduleId);
    if (index === -1) return;

    const newModules = [...courseModules];
    if (direction === 'up' && index > 0) {
      [newModules[index - 1], newModules[index]] = [newModules[index], newModules[index - 1]];
      newModules[index - 1].order = index - 1;
      newModules[index].order = index;
    } else if (direction === 'down' && index < newModules.length - 1) {
      [newModules[index], newModules[index + 1]] = [newModules[index + 1], newModules[index]];
      newModules[index].order = index;
      newModules[index + 1].order = index + 1;
    }
    setCourseModules(newModules);
  };

  const handleSaveModules = async (e) => {
    e.preventDefault();
    
    // Remove all existing modules first, then add new ones
    const removePromises = selectedCourse.courseModules?.map(cm => 
      coursesAPI.removeModule(selectedCourse.id, cm.moduleId || cm.module?.id)
    ).filter(Boolean) || [];

    await Promise.all(removePromises);

    // Add modules in order
    const addPromises = courseModules.map((cm, idx) =>
      coursesAPI.addModule(selectedCourse.id, cm.moduleId, idx)
    );

    const response = await execute(async () => {
      await Promise.all(addPromises);
      return { success: true };
    }, { 
      successMessage: 'Course modules updated successfully!' 
    });
    
    if (response) {
      setShowModulesModal(false);
      setSelectedCourse(null);
      fetchCourses();
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return getCourseCategoryDisplayName(category);
  };
  
  const getCategoryDisplayName = (category) => {
    return getCourseCategoryDisplayName(category);
  };

  const courseCounts = useMemo(() => {
    const total = courses.length;
    const online = courses.filter((c) => c.deliveryMode === 'Online').length;
    const offline = courses.filter((c) => c.deliveryMode === 'Offline').length;
    const both = courses.filter((c) => c.deliveryMode === 'Both').length;
    return { total, online, offline, both };
  }, [courses]);

  const filteredCourses = useMemo(() => {
    if (deliveryModeFilter === 'All') return courses;
    return courses.filter((course) => course.deliveryMode === deliveryModeFilter);
  }, [courses, deliveryModeFilter]);

  return (
    <StaffPageShell title="Courses" wide>
        <div className="flex justify-end items-center mb-6">
          <button
            onClick={handleCreate}
            className="px-6 py-2 rounded-lg font-medium theme-button-primary flex items-center gap-2"
          >
            <span>+</span> Create Course
          </button>
        </div>

        <MessageDisplay error={error} success={success} />

        {loading && <LoadingSpinner />}

        <div className="mb-6 p-4 rounded-lg theme-card border border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
              Total: {courseCounts.total}
            </span>
            <span className="inline-flex px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
              Online: {courseCounts.online}
            </span>
            <span className="inline-flex px-3 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-800">
              Offline: {courseCounts.offline}
            </span>
            <span className="inline-flex px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
              Both: {courseCounts.both}
            </span>
            <span className="inline-flex text-sm theme-text-secondary">
              Showing: {filteredCourses.length}
            </span>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-end gap-3">
            <div className="w-full md:w-64">
              <label className="block text-sm font-medium mb-1 theme-text">Filter by Delivery Type</label>
              <SearchableSelect
                value={deliveryModeFilter}
                onChange={(e) => setDeliveryModeFilter(e.target.value)}
                className="w-full"
                required
                options={[
                  { value: 'All', label: 'All' },
                  { value: 'Online', label: 'Online' },
                  { value: 'Offline', label: 'Offline' },
                  { value: 'Both', label: 'Both' }
                ]}
              />
            </div>

            <div className="w-full md:w-auto md:ml-auto">
              <button
                type="button"
                onClick={() => setDeliveryModeFilter('All')}
                className="w-full px-4 py-2 rounded-lg font-medium theme-button-secondary"
              >
                Reset Filter
              </button>
            </div>
          </div>
        </div>

        <div className="theme-card rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.isArray(filteredCourses) && filteredCourses.map((course) => (
              <div
                key={course.id}
                className="p-4 border rounded-lg theme-border hover:shadow-lg transition-shadow"
                style={{ backgroundColor: 'var(--color-surface)' }}
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-semibold theme-text">{course.courseName}</h3>
                    {course.courseCode && (
                      <p className="text-sm theme-text-secondary">Code: {course.courseCode}</p>
                    )}
                    <p className="text-xs theme-text-secondary mt-1">
                      Category: {course.category ? getCategoryDisplayName(course.category) : getCategoryName(course.categoryId)}
                    </p>
                  </div>
                  <span
                    className={`px-2 py-1 rounded text-xs ${
                      course.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {course.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                
                <div className="space-y-1 text-sm theme-text-secondary mb-3">
                  <p>Mode: {course.deliveryMode}</p>
                  {course.deliveryMode === 'Offline' && course.offlineCenter && <p>Center: {course.offlineCenter}</p>}
                  {course.durationDays && <p>Duration: {course.durationDays} days</p>}
                  <p>Modules: {course.courseModules?.length || course.trainingModules?.length || 0}</p>
                </div>

                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => handleEdit(course)}
                    className="flex-1 px-3 py-1.5 text-sm rounded theme-button-secondary"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleManageModules(course)}
                    className="flex-1 px-3 py-1.5 text-sm rounded theme-button-primary"
                  >
                    Manage Modules
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredCourses.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="theme-text-secondary">No courses found for selected filter.</p>
            </div>
          )}
        </div>

        {/* Create/Edit Modal */}
        <Modal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingCourse(null);
            setFieldErrors({});
            setError('');
          }}
          title={editingCourse ? 'Edit Course' : 'Create Course'}
          size="large"
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium theme-text mb-1">
                Course Name *
              </label>
              <input
                type="text"
                required
                value={formData.courseName}
                onChange={(e) => {
                  setFormData({ ...formData, courseName: e.target.value });
                  if (fieldErrors.courseName) {
                    setFieldErrors({ ...fieldErrors, courseName: '' });
                  }
                }}
                className={`w-full px-3 py-2 rounded-lg theme-input ${
                  fieldErrors.courseName ? 'border-2 border-red-500' : ''
                }`}
                placeholder="e.g., VLSI Physical Design"
              />
              {fieldErrors.courseName && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.courseName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium theme-text mb-1">
                Course Code
              </label>
              <input
                type="text"
                value={formData.courseCode}
                onChange={(e) => {
                  setFormData({ ...formData, courseCode: e.target.value.toUpperCase() });
                  if (fieldErrors.courseCode) {
                    setFieldErrors({ ...fieldErrors, courseCode: '' });
                  }
                }}
                className={`w-full px-3 py-2 rounded-lg theme-input ${
                  fieldErrors.courseCode ? 'border-2 border-red-500' : ''
                }`}
                placeholder="e.g., VLSI-PD-001"
              />
              {fieldErrors.courseCode && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.courseCode}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium theme-text mb-1">
                Category *
              </label>
              <SearchableSelect
                required
                value={formData.categoryId}
                onChange={(e) => {
                  setFormData({ ...formData, categoryId: e.target.value });
                  if (fieldErrors.categoryId) {
                    setFieldErrors({ ...fieldErrors, categoryId: '' });
                  }
                }}
                className="w-full"
                error={Boolean(fieldErrors.categoryId)}
                placeholder="Select Category"
                options={categories}
                getOptionValue={(cat) => cat.id}
                getOptionLabel={(cat) => {
                  const catName = cat.categoryName || cat.name || '';
                  const catCode = cat.categoryCode || cat.code || '';
                  return catCode ? `${catName} - ${catCode}` : catName;
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium theme-text mb-1">
                Duration (Days)
              </label>
              <input
                type="number"
                value={formData.durationDays}
                onChange={(e) => setFormData({ ...formData, durationDays: e.target.value })}
                className="w-full px-3 py-2 rounded-lg theme-input"
                placeholder="e.g., 90"
                min="1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium theme-text mb-1">
                Course Delivery Type *
              </label>
              <SearchableSelect
                required
                value={formData.deliveryMode}
                onChange={(e) => {
                  setFormData({ ...formData, deliveryMode: e.target.value, offlineCenter: e.target.value === 'Offline' ? formData.offlineCenter : '' });
                  if (fieldErrors.deliveryMode) {
                    setFieldErrors({ ...fieldErrors, deliveryMode: '' });
                  }
                }}
                className="w-full"
                error={Boolean(fieldErrors.deliveryMode)}
                options={[
                  { value: 'Online', label: 'Online' },
                  { value: 'Offline', label: 'Offline' },
                  { value: 'Both', label: 'Both (Online + Offline)' }
                ]}
              />
              {fieldErrors.deliveryMode && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.deliveryMode}</p>
              )}
            </div>

            {formData.deliveryMode === 'Offline' && (
              <div>
                <label className="block text-sm font-medium theme-text mb-1">
                  Offline Center *
                </label>
                <SearchableSelect
                  required={formData.deliveryMode === 'Offline'}
                  value={formData.offlineCenter}
                  onChange={(e) => {
                    setFormData({ ...formData, offlineCenter: e.target.value });
                    if (fieldErrors.offlineCenter) {
                      setFieldErrors({ ...fieldErrors, offlineCenter: '' });
                    }
                  }}
                  className="w-full"
                  error={Boolean(fieldErrors.offlineCenter)}
                  placeholder="Select Center"
                  creatable
                  options={centerOptionsWithFallback(catalogCenters, [], [formData.offlineCenter])}
                />
                {fieldErrors.offlineCenter && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.offlineCenter}</p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium theme-text mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => {
                  setFormData({ ...formData, description: e.target.value });
                  if (fieldErrors.description) {
                    setFieldErrors({ ...fieldErrors, description: '' });
                  }
                }}
                className={`w-full px-3 py-2 rounded-lg theme-input ${
                  fieldErrors.description ? 'border-2 border-red-500' : ''
                }`}
                rows="3"
                placeholder="Course description..."
              />
              {fieldErrors.description && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.description}</p>
              )}
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="mr-2"
              />
              <label htmlFor="isActive" className="text-sm theme-text">
                Active
              </label>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 rounded-lg font-medium theme-button-primary disabled:opacity-50"
              >
                {loading ? 'Saving...' : editingCourse ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setEditingCourse(null);
                }}
                className="flex-1 px-4 py-2 rounded-lg font-medium theme-button-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </Modal>

        {/* Manage Modules Modal */}
        <Modal
          isOpen={showModulesModal}
          onClose={() => {
            setShowModulesModal(false);
            setSelectedCourse(null);
          }}
          title={`Manage Modules - ${selectedCourse?.courseName || ''}`}
          size="large"
        >
          <form onSubmit={handleSaveModules} className="space-y-4">
            <div>
              <label className="block text-sm font-medium theme-text mb-2">
                Add Module
              </label>
              <div className="flex gap-2">
                <SearchableSelect
                  value={moduleToAdd}
                  onChange={(e) => {
                    if (e.target.value) {
                      handleAddModule(parseInt(e.target.value));
                    }
                    setModuleToAdd('');
                  }}
                  className="flex-1"
                  placeholder="Select module to add..."
                  options={availableModules}
                  getOptionValue={(module) => module.id}
                  getOptionLabel={(module) => module.moduleName}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium theme-text mb-2">
                Course Modules (Drag to reorder or use arrows)
              </label>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {courseModules.length === 0 ? (
                  <p className="text-sm theme-text-secondary text-center py-4">
                    No modules added yet. Select a module above to add.
                  </p>
                ) : (
                  courseModules.map((cm, index) => (
                    <div
                      key={cm.moduleId}
                      className="flex items-center gap-2 p-2 border rounded-lg theme-border"
                      style={{ backgroundColor: 'var(--color-surface)' }}
                    >
                      <span className="text-sm theme-text-secondary w-6">{index + 1}.</span>
                      <span className="flex-1 theme-text">{cm.moduleName}</span>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => handleUpdateModuleOrder(cm.moduleId, 'up')}
                          disabled={index === 0}
                          className="px-2 py-1 text-xs rounded theme-button-secondary disabled:opacity-50"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => handleUpdateModuleOrder(cm.moduleId, 'down')}
                          disabled={index === courseModules.length - 1}
                          className="px-2 py-1 text-xs rounded theme-button-secondary disabled:opacity-50"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveModule(cm.moduleId)}
                          className="px-2 py-1 text-xs rounded bg-red-500 text-white hover:bg-red-600"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 rounded-lg font-medium theme-button-primary disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Modules'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowModulesModal(false);
                  setSelectedCourse(null);
                }}
                className="flex-1 px-4 py-2 rounded-lg font-medium theme-button-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </Modal>
    </StaffPageShell>
  );
};

export default CourseManagementPage;
